import { ensureTrailingSlash } from "@commercekit/shared";
import { asText } from "@prismicio/helpers";

export const metaHelper = ({ data }: { data?: Record<string, any> }) => {
  const { seo, canonicalsUrl, currentPage, paginationPage, result } = data || {};
  // WOV-2195: on `?page=2` and deeper, the canonical and the alternates point at
  // that same page instead of page 1, which would read as a duplicate.
  const page = typeof paginationPage === "number" && paginationPage > 1 ? paginationPage : 1;
  const withPage = (url: string) => (page > 1 ? `${url}?page=${page}` : url);
  const tags: { 
    tagName?: string;
    rel?: string;
    hrefLang?: string;
    href?: string;
    title?: string;
    name?: string;
    content?: string;
  }[] = [];

  // Prefer the SEO metaTitle, but fall back to the document's page title
  // (a Rich Text field exposed under `data.title` on some pages) so the
  // browser tab never shows an empty/undefined title.
  const title =
    (seo && "title" in seo ? seo.title : undefined) ||
    (Array.isArray(data?.title) ? asText(data.title) : data?.title);
  const { description, keywords } = seo || {};

  if (title) {
    tags.push({ title }, { name: "title", content: title });
  }
  if (description) {
    tags.push({ name: "description", content: description });
  }
  if (keywords) {
    tags.push({ name: "keywords", content: keywords });
  }
  if (canonicalsUrl) {
    tags.push({
      tagName: "link",
      rel: "canonical",
      href: withPage(normalizeCanonicalUrl(canonicalsUrl)),
    });
  }
  // Normalize and add hreflang tags for all page types (including current locale for SEO)
  const alternateLinks: {
    hrefLang?: string;
    href?: string;
    locale?: string;
    url?: string;
  }[] =
    (currentPage === "PRODUCT_DETAILS_PAGE" &&
      result?.productInfo?.localizedSlugs) ||
    (currentPage === "CATEGORY_PAGE" && result?.categoryData?.localizedSlugs) ||
    (currentPage === "BLOG_CONTENT_PAGE" && result?.alternateLanguageLinks) ||
    data?.alternateLanguageLinks ||
    [];

  alternateLinks.forEach((link) => {
    const hrefLang = link.locale || link.hrefLang;
    const href = link.url || link.href;
    const pagedHref = href ? withPage(href) : href;

    if (hrefLang && pagedHref && !tags.some((tag) => tag.hrefLang === hrefLang && tag.href === pagedHref)) {
      tags.push({
        tagName: "link",
        rel: "alternate",
        hrefLang,
        href: pagedHref,
      });
    }
  });
  return tags;
};

export const normalizeCanonicalUrl = (url: string) => {
  const secureUrl =
    /http:\/\/localhost/.test(url) || url.includes("https://")
      ? url
      : url.replace("http://", "https://");

  return ensureTrailingSlash(secureUrl);
};
